import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, User, Eye, Settings, X, Check, LogOut, Play, Trash2, UserPlus, Shield, Lock, Unlock, Minus, Plus, RefreshCw } from 'lucide-react';
import { ThemedBackground } from '../components/ThemedBackground';
import { auth, UserInfo } from '../lib/auth';
import { supabase, waitForSupabaseAuthReady } from '../lib/supabase';
import { GAME_CONFIGS, ROLES, RoleType } from '../types';

interface RoleConfig {
  roleId: RoleType;
  count: number;
}

interface GameOptions {
  enableExcalibur: boolean;
  forcedRound: 'single3' | 'single5' | 'global3';
  enableLadyOfLake: boolean;
}

const getDefaultRoleConfigs = (playerCount: number): RoleConfig[] => {
  const ROLE_SETUPS: Record<number, RoleConfig[]> = {
    5: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 1 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
    ],
    6: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 2 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
    ],
    7: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 2 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'oberon', count: 1 },
    ],
    8: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 3 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'minion', count: 1 },
    ],
    9: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 4 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'mordred', count: 1 },
    ],
    10: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 4 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'mordred', count: 1 },
      { roleId: 'oberon', count: 1 },
    ],
    11: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 5 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'mordred', count: 1 },
      { roleId: 'minion', count: 1 },
    ],
    12: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 5 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'mordred', count: 1 },
      { roleId: 'oberon', count: 2 },
    ],
    13: [
      { roleId: 'merlin', count: 1 },
      { roleId: 'percival', count: 1 },
      { roleId: 'loyal', count: 6 },
      { roleId: 'morgana', count: 1 },
      { roleId: 'assassin', count: 1 },
      { roleId: 'mordred', count: 1 },
      { roleId: 'oberon', count: 1 },
      { roleId: 'minion', count: 1 },
    ],
  };
  return ROLE_SETUPS[playerCount] || [];
};

type RoomRole = 'host' | 'player' | 'spectator';

interface RoomPlayer {
  id: number;
  player_id: number;
  player_nickname: string;
  avatar_url?: string;
  seat_number: number | null;
  role: RoomRole;
  is_ready: boolean;
  is_host: boolean;
  is_spectator: boolean;
  join_time: string;
}

interface RoomInfo {
  id: number;
  room_id: string;
  room_name: string;
  host_player_id: number;
  game_mode: string;
  max_players: number;
  min_players: number;
  is_private: boolean;
  room_password?: string;
  status: 'waiting' | 'playing' | 'finished';
  created_at: string;
  role_configs?: RoleConfig[];
  game_options?: GameOptions;
}

interface RoleConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerCount: number;
  roleConfigs: RoleConfig[];
  onSave: (configs: RoleConfig[]) => void;
}

function RoleConfigModal({ isOpen, onClose, playerCount, roleConfigs, onSave }: RoleConfigModalProps) {
  const [localConfigs, setLocalConfigs] = useState<RoleConfig[]>(roleConfigs);

  useEffect(() => {
    setLocalConfigs(roleConfigs);
  }, [roleConfigs]);

  if (!isOpen) return null;

  const updateRoleCount = (roleId: RoleType, delta: number) => {
    const newConfigs = localConfigs.map((config) => {
      if (config.roleId === roleId) {
        const newCount = Math.max(0, config.count + delta);
        return { ...config, count: newCount };
      }
      return config;
    });
    setLocalConfigs(newConfigs);
  };

  const getTotalRoles = () => {
    return localConfigs.reduce((sum, config) => sum + config.count, 0);
  };

  const handleSave = () => {
    onSave(localConfigs);
    onClose();
  };

  const availableGoodRoles: RoleType[] = ['merlin', 'percival', 'loyal', 'goodLancelot'];
  const availableEvilRoles: RoleType[] = ['mordred', 'morgana', 'assassin', 'minion', 'oberon', 'evilLancelot'];

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-avalon-card rounded-xl p-5 w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">自定义角色配置</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-blue-400 text-sm font-medium">正义阵营</span>
          </div>
          <div className="space-y-2">
            {availableGoodRoles.map((roleId) => {
              const config = localConfigs.find(c => c.roleId === roleId);
              if (!config) return null;
              const role = ROLES[roleId];
              const isZeroCount = config.count === 0;
              return (
                <div
                  key={roleId}
                  className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
                    isZeroCount
                      ? 'bg-transparent border-blue-500/20'
                      : 'bg-blue-500/20 border-blue-500/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{role.icon}</span>
                    <div className="text-white text-sm font-medium">
                      {role.name} <span className="text-avalon-gold ml-1">×{config.count}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateRoleCount(roleId, -1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-white font-medium w-5 text-center text-sm">{config.count}</span>
                    <button
                      onClick={() => updateRoleCount(roleId, 1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-red-400 text-sm font-medium">邪恶阵营</span>
          </div>
          <div className="space-y-2">
            {availableEvilRoles.map((roleId) => {
              const config = localConfigs.find(c => c.roleId === roleId);
              if (!config) return null;
              const role = ROLES[roleId];
              const isZeroCount = config.count === 0;
              return (
                <div
                  key={roleId}
                  className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
                    isZeroCount
                      ? 'bg-transparent border-red-500/20'
                      : 'bg-red-500/20 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{role.icon}</span>
                    <div className="text-white text-sm font-medium">
                      {role.name} <span className="text-avalon-gold ml-1">×{config.count}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateRoleCount(roleId, -1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-white font-medium w-5 text-center text-sm">{config.count}</span>
                    <button
                      onClick={() => updateRoleCount(roleId, 1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex gap-2 pt-4 border-t border-avalon-purple/20">
          <button
            onClick={() => {
              const defaultConfigs = getDefaultRoleConfigs(playerCount);
              setLocalConfigs(defaultConfigs);
            }}
            className="flex-1 py-2 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple transition-colors"
          >
            恢复默认
          </button>
          <button
            onClick={handleSave}
            disabled={getTotalRoles() !== playerCount}
            className="flex-1 py-2 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            保存配置
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OnlineGameRoom() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [players, setPlayers] = useState<RoomPlayer[]>([]);
  const [currentPlayer, setCurrentPlayer] = useState<RoomPlayer | null>(null);
  const [loading, setLoading] = useState(true);

  
  const [playerCount, setPlayerCount] = useState<number>(5);
  const [isGameLocked, setIsGameLocked] = useState(false);
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(false);
  const [showRoleConfigModal, setShowRoleConfigModal] = useState(false);
  const [roleConfigs, setRoleConfigs] = useState<RoleConfig[]>([]);
  const [gameOptions, setGameOptions] = useState<GameOptions>({
    enableExcalibur: false,
    forcedRound: 'single3',
    enableLadyOfLake: false,
  });
  
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showCloseRoomConfirm, setShowCloseRoomConfirm] = useState(false);
  const [playerProfiles, setPlayerProfiles] = useState<Record<number, { avatar_url?: string }>>({});
  const [isRestoringState, setIsRestoringState] = useState(true);

  // 从 sessionStorage 恢复状态（优先于数据库加载）
  useEffect(() => {
    const restoreState = () => {
      try {
        const savedState = sessionStorage.getItem(`room_${roomId}_state`);
        if (savedState) {
          const parsed = JSON.parse(savedState);
          // 检查是否过期（30 分钟）
          const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
          if (!parsed.timestamp || parsed.timestamp < thirtyMinutesAgo) {
            console.log('[restoreState] 缓存已过期，忽略');
            sessionStorage.removeItem(`room_${roomId}_state`);
            return;
          }
          
          console.log('[restoreState] 恢复本地缓存配置', parsed);
          if (parsed.playerCount) setPlayerCount(parsed.playerCount);
          if (parsed.roleConfigs) setRoleConfigs(parsed.roleConfigs);
          if (parsed.gameOptions) setGameOptions(parsed.gameOptions);
          if (parsed.isGameLocked !== undefined) setIsGameLocked(parsed.isGameLocked);
          if (parsed.configSectionCollapsed !== undefined) setConfigSectionCollapsed(parsed.configSectionCollapsed);
        }
      } catch (err) {
        console.error('[restoreState] 恢复状态失败:', err);
      } finally {
        setIsRestoringState(false);
      }
    };
    
    restoreState();
  }, [roomId]);

  // 保存状态到 sessionStorage
  useEffect(() => {
    if (!isRestoringState || !roomInfo) return;
    
    try {
      const stateToSave = {
        playerCount,
        roleConfigs,
        gameOptions,
        isGameLocked,
        configSectionCollapsed,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(`room_${roomId}_state`, JSON.stringify(stateToSave));
    } catch (err) {
      console.error('[saveState] 保存状态失败:', err);
    }
  }, [playerCount, roleConfigs, gameOptions, isGameLocked, configSectionCollapsed, isRestoringState, roomInfo, roomId]);

  // 清理过期状态（超过 30 分钟）
  useEffect(() => {
    if (!roomId) return;
    
    try {
      const savedState = sessionStorage.getItem(`room_${roomId}_state`);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
        if (parsed.timestamp && parsed.timestamp < thirtyMinutesAgo) {
          sessionStorage.removeItem(`room_${roomId}_state`);
        }
      }
    } catch (err) {
      console.error('[cleanupExpiredState] 清理过期状态失败:', err);
    }
  }, [roomId]);

  // 从数据库加载房间信息后，根据情况决定是否应用数据库配置
  useEffect(() => {
    if (!roomInfo || isRestoringState) return;
    
    // 如果已经从本地缓存恢复了状态，就不再应用数据库的配置（避免覆盖用户的临时修改）
    // 只有当用户是第一次进入房间（没有本地缓存）时，才使用数据库的配置
    const savedState = sessionStorage.getItem(`room_${roomId}_state`);
    const hasLocalCache = savedState !== null;
    
    if (hasLocalCache) {
      console.log('[loadRoomConfig] 存在本地缓存，跳过数据库配置加载');
      return;
    }
    
    console.log('[loadRoomConfig] 无本地缓存，使用数据库配置');
    setPlayerCount(roomInfo.min_players);
    if (roomInfo.role_configs && roomInfo.role_configs.length > 0) {
      setRoleConfigs(roomInfo.role_configs);
    } else {
      const defaultConfigs = getDefaultRoleConfigs(roomInfo.min_players);
      setRoleConfigs(defaultConfigs);
    }
    if (roomInfo.game_options) {
      setGameOptions(roomInfo.game_options);
    }
  }, [roomInfo, isRestoringState, roomId]);

  const updatePlayerCount = async (count: number) => {
    if (!currentPlayer?.is_host) return; // 只有房主能修改
    if (isGameLocked) return;
    setPlayerCount(count);
    
    const defaultConfigs = getDefaultRoleConfigs(count);
    const allRoleIds: RoleType[] = ['merlin', 'percival', 'loyal', 'goodLancelot', 'mordred', 'morgana', 'assassin', 'minion', 'oberon', 'evilLancelot'];
    
    const completeConfigs = allRoleIds.map(roleId => {
      const existing = defaultConfigs.find(c => c.roleId === roleId);
      return existing || { roleId, count: 0 };
    });
    
    setRoleConfigs(completeConfigs);
    
    if (roomInfo && currentPlayer?.is_host) {
      try {
        await waitForSupabaseAuthReady();
        const { error } = await supabase.rpc('update_room_player_count', {
          p_room_id: roomId,
          p_player_count: count
        });
        
        if (error) {
          console.error('[updatePlayerCount] 更新失败:', error);
        } else {
          setRoomInfo(prev => prev ? { ...prev, min_players: count } : null);
        }
        
        // 同时保存角色配置到数据库
        const { error: configError } = await supabase
          .from('online_game_rooms_cce0d86f_8f5')
          .update({ role_configs: completeConfigs })
          .eq('id', roomInfo.id);
        
        if (configError) {
          console.error('[updatePlayerCount] 保存角色配置失败:', configError);
        }
      } catch (err) {
        console.error('[updatePlayerCount] 异常:', err);
      }
    }
  };

  const updateGameOption = async <K extends keyof GameOptions>(key: K, value: GameOptions[K]) => {
    if (!currentPlayer?.is_host) return; // 只有房主能修改
    setGameOptions(prev => ({ ...prev, [key]: value }));
    
    if (!roomInfo?.id) return;
    
    try {
      await waitForSupabaseAuthReady();
      const updatedOptions = { ...gameOptions, [key]: value };
      
      const { error } = await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .update({ game_options: updatedOptions })
        .eq('id', roomInfo.id);
      
      if (error) {
        console.error('Failed to save game options:', error);
      }
    } catch (err) {
      console.error('Error saving game options:', err);
    }
  };

  const saveRoleConfigs = async (newConfigs: RoleConfig[]) => {
    if (!currentPlayer?.is_host) return; // 只有房主能修改
    setRoleConfigs(newConfigs);
    
    if (!roomInfo?.id) return;
    
    try {
      await waitForSupabaseAuthReady();
      
      const { error } = await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .update({ role_configs: newConfigs })
        .eq('id', roomInfo.id);
      
      if (error) {
        console.error('Failed to save role configs:', error);
      }
    } catch (err) {
      console.error('Error saving role configs:', err);
    }
  };

  useEffect(() => {
    auth.subscribe((user) => {
      setCurrentUser(user);
      if (!user) {
        navigate('/online-lobby');
      }
    });

    loadRoomData();
    
    return () => {
      if (roomId) {
        const channel = supabase.channel(`room:${roomId}`);
        supabase.removeChannel(channel);
      }
    };
  }, [roomId, navigate]);
  
  useEffect(() => {
    if (!roomId || !roomInfo?.id) return;
    
    const channel = supabase.channel(`room:${roomId}`);
    
    channel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'online_game_rooms_cce0d86f_8f5',
        filter: `id=eq.${roomInfo.id}`
      },
      (payload) => {
        const updatedRoom = payload.new as RoomInfo;
        setRoomInfo(updatedRoom);
        
        // 检查是否有本地缓存，如果有则不覆盖用户的临时配置
        const savedState = sessionStorage.getItem(`room_${roomId}_state`);
        const hasLocalCache = savedState !== null;
        
        if (!hasLocalCache) {
          // 无本地缓存时，同步数据库配置
          if (updatedRoom.min_players !== playerCount) {
            setPlayerCount(updatedRoom.min_players);
          }
          if (updatedRoom.role_configs) {
            setRoleConfigs(updatedRoom.role_configs);
          }
          if (updatedRoom.game_options) {
            setGameOptions(updatedRoom.game_options);
          }
        } else {
          // 有本地缓存时，只同步玩家人数（因为这是房间级别的状态）
          if (updatedRoom.min_players !== playerCount) {
            setPlayerCount(updatedRoom.min_players);
          }
          console.log('[realtime] 存在本地缓存，忽略数据库配置更新');
        }
      }
    );
    
    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'online_room_players_cce0d86f_8f5',
        filter: `room_id=eq.${roomInfo.id}`
      },
      async (payload) => {
        console.log('[Realtime] Player change:', payload.eventType, payload);
        if (payload.eventType === 'DELETE') {
          setPlayers(prev => prev.filter(p => p.id !== payload.old.id));
        } else {
          // INSERT or UPDATE - reload player data for this room only
          await loadRoomData();
        }
      }
    );
    
    channel.subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId, roomInfo?.id]);

  useEffect(() => {
    if (currentUser && roomId) {
      const currentNickname = (currentUser.nickname || '').trim().toLowerCase();
      const player = players.find(p => (p.player_nickname || '').trim().toLowerCase() === currentNickname);
      setCurrentPlayer(player || null);
    }
  }, [currentUser, players, roomId]);

  const loadRoomData = async (forceRefresh: boolean = false) => {
    try {
      await waitForSupabaseAuthReady();
      setLoading(true);

      if (!roomId) {
        console.error('No room ID provided');
        setLoading(false);
        return;
      }

      // 支持 numeric id 或 UUID room_id
      const isNumericId = /^\d+$/.test(roomId);
      let roomData: any = null;
      
      if (isNumericId) {
        // 优先尝试 numeric id
        const { data, error } = await supabase
          .from('online_game_rooms_cce0d86f_8f5')
          .select('*')
          .eq('id', parseInt(roomId))
          .eq('is_deleted', 'n')
          .single();
        
        if (error) {
          // 如果 numeric id 找不到，尝试 room_id
          const { data: uuidData, error: uuidError } = await supabase
            .from('online_game_rooms_cce0d86f_8f5')
            .select('*')
            .eq('room_id', roomId)
            .eq('is_deleted', 'n')
            .single();
          
          if (uuidError || !uuidData) {
            console.error('Failed to load room:', error);
            setLoading(false);
            return;
          }
          roomData = uuidData;
        } else {
          roomData = data;
        }
      } else {
        // UUID room_id
        const { data, error } = await supabase
          .from('online_game_rooms_cce0d86f_8f5')
          .select('*')
          .eq('room_id', roomId)
          .eq('is_deleted', 'n')
          .single();
        
        if (error || !data) {
          console.error('Failed to load room:', error);
          setLoading(false);
          return;
        }
        roomData = data;
      }

      setRoomInfo(roomData);
      
      // 如果是强制刷新（用户点击刷新按钮），保留本地缓存
      // 如果不是强制刷新（首次加载或其他情况），则根据缓存决定
      const savedState = sessionStorage.getItem(`room_${roomId}_state`);
      const hasLocalCache = savedState !== null;
      
      if (forceRefresh) {
        console.log('[loadRoomData] 强制刷新，保留本地缓存配置');
        // 强制刷新时，只更新房间信息，不改变用户的配置
      } else if (!hasLocalCache) {
        console.log('[loadRoomData] 无本地缓存，使用数据库配置');
        const dbPlayerCount = roomData.min_players || 5;
        const dbRoleConfigs = roomData.role_configs && roomData.role_configs.length > 0 
          ? roomData.role_configs 
          : getDefaultRoleConfigs(dbPlayerCount);
        const dbGameOptions = roomData.game_options || {
          enableExcalibur: false,
          forcedRound: 'single3',
          enableLadyOfLake: false,
        };
        
        setPlayerCount(dbPlayerCount);
        setRoleConfigs(dbRoleConfigs);
        setGameOptions(dbGameOptions);
      } else {
        console.log('[loadRoomData] 存在本地缓存，保留用户配置');
      }

      const { data: playersData, error: playersError } = await supabase
        .from('online_room_players_cce0d86f_8f5')
        .select('*')
        .eq('room_id', roomData.id)
        .eq('is_deleted', 'n')
        .order('id', { ascending: true });

      if (playersError) {
        console.error('Failed to load players:', playersError);
        setLoading(false);
        return;
      }

      // 安全处理可能不存在的字段
      const playersWithSeats = (playersData || []).map((p) => ({
        ...p,
        role: ((p as any).is_spectator ? 'spectator' : p.is_host || (p as any).player_role === 'host' ? 'host' : 'player') as RoomRole,
      }));

      setPlayers(playersWithSeats);
      
      const playerIds = playersWithSeats.map(p => p.player_id).filter(Boolean);
      if (playerIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('player_profiles_cce0d86f_8f5')
          .select('id, avatar_url')
          .in('id', playerIds);
        
        if (profilesData) {
          const profilesMap: Record<number, { avatar_url?: string }> = {};
          profilesData.forEach(profile => {
            profilesMap[profile.id] = { avatar_url: profile.avatar_url || undefined };
          });
          setPlayerProfiles(profilesMap);
        }
      }
    } catch (error) {
      console.error('Error loading room data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExitRoom = async () => {
    try {
      await waitForSupabaseAuthReady();
      
      if (!currentPlayer || !roomInfo) return;
      
      // 获取玩家 ID
      const { data: playerProfile } = await supabase.rpc(
        'get_or_create_player_profile',
        { p_nickname: currentPlayer.player_nickname }
      );
      
      if (!playerProfile) {
        console.error('Failed to get player profile');
        navigate('/online-lobby');
        return;
      }
      
      const playerId = playerProfile as number;
      
      if (currentPlayer.is_spectator) {
        const { error } = await supabase.rpc('leave_as_spectator', {
          p_room_id: roomInfo.id,
          p_player_id: playerId
        });
        if (error) throw error;
      } else {
        const result = await supabase.rpc('leave_online_room', {
          p_room_id: roomInfo.id,
          p_player_id: playerId
        });
        
        if (result.error) throw result.error;
        
        // 如果房主离开解散了房间，直接跳转
        if (result.data?.action === 'room_dissolved') {
          navigate('/online-lobby');
          return;
        }
      }

      navigate('/online-lobby');
      
      // 清理 sessionStorage
      if (roomId) {
        sessionStorage.removeItem(`room_${roomId}_state`);
      }
    } catch (error) {
      console.error('Failed to exit room:', error);
      navigate('/online-lobby');
      
      // 清理 sessionStorage
      if (roomId) {
        sessionStorage.removeItem(`room_${roomId}_state`);
      }
    }
  };

  const handleCloseRoom = async () => {
    try {
      await waitForSupabaseAuthReady();
      
      if (!roomInfo || !currentPlayer) return;

      // 获取玩家 ID
      const { data: playerProfile } = await supabase.rpc(
        'get_or_create_player_profile',
        { p_nickname: currentPlayer.player_nickname }
      );
      
      if (!playerProfile) {
        console.error('Failed to get player profile');
        return;
      }
      
      const playerId = playerProfile as number;

      const result = await supabase.rpc('leave_online_room', {
        p_room_id: roomInfo.id,
        p_player_id: playerId
      });

      if (result.error) throw result.error;

      navigate('/online-lobby');
      
      // 清理 sessionStorage
      if (roomId) {
        sessionStorage.removeItem(`room_${roomId}_state`);
      }
    } catch (error) {
      console.error('Failed to close room:', error);
      
      // 清理 sessionStorage
      if (roomId) {
        sessionStorage.removeItem(`room_${roomId}_state`);
      }
    }
  };

  const handleKickPlayer = async (playerNickname: string) => {
    try {
      await waitForSupabaseAuthReady();
      
      if (!roomInfo) return;
      
      const result = await supabase.rpc('kick_player', {
        p_room_id: roomInfo.id,
        p_target_player_nickname: playerNickname
      });
      
      if (result.error) throw result.error;
      
      if (result.data?.success) {
        setPlayers(players.filter(p => p.player_nickname !== playerNickname));
      } else {
        console.error('Kick failed:', result.data?.error);
      }
    } catch (error) {
      console.error('Failed to kick player:', error);
    }
  };

  const handleSeatClick = async (seatNumber: number) => {
    if (!currentPlayer || !roomInfo) return;

    const targetSeatPlayer = players.find(p => p.seat_number === seatNumber);

    if (targetSeatPlayer) {
      if (targetSeatPlayer.player_nickname === currentPlayer.player_nickname) {
        await leaveSeat();
      }
      return;
    }

    try {
      await waitForSupabaseAuthReady();

      // 获取玩家 ID
      const { data: playerProfile } = await supabase.rpc(
        'get_or_create_player_profile',
        { p_nickname: currentPlayer.player_nickname }
      );
      
      if (!playerProfile) {
        console.error('Failed to get player profile');
        return;
      }
      
      const playerId = playerProfile as number;

      const { error } = await supabase.rpc('assign_player_seat', {
        p_room_id: roomInfo.id,
        p_player_id: playerId,
        p_seat_number: seatNumber
      });

      if (error) throw error;

      setPlayers(players.map(p => 
        p.player_nickname === currentPlayer.player_nickname
          ? { ...p, seat_number: seatNumber }
          : p.seat_number === seatNumber 
            ? { ...p, seat_number: null }
            : p
      ));
    } catch (error) {
      console.error('Failed to change seat:', error);
    }
  };

  const leaveSeat = async () => {
    if (!currentPlayer || !roomInfo) return;

    try {
      await waitForSupabaseAuthReady();

      // 获取玩家 ID
      const { data: playerProfile } = await supabase.rpc(
        'get_or_create_player_profile',
        { p_nickname: currentPlayer.player_nickname }
      );
      
      if (!playerProfile) {
        console.error('Failed to get player profile');
        return;
      }
      
      const playerId = playerProfile as number;

      const { error } = await supabase
        .from('online_room_players_cce0d86f_8f5')
        .update({ seat_number: null, updated_at: new Date() })
        .eq('player_id', playerId);

      if (error) throw error;

      setPlayers(players.map(p => 
        p.player_nickname === currentPlayer.player_nickname
          ? { ...p, seat_number: null }
          : p
      ));
    } catch (error) {
      console.error('Failed to leave seat:', error);
    }
  };

  const handleToggleReady = async () => {
    if (!currentPlayer || !roomInfo) return;

    try {
      await waitForSupabaseAuthReady();

      // 获取玩家 ID
      const { data: playerProfile } = await supabase.rpc(
        'get_or_create_player_profile',
        { p_nickname: currentPlayer.player_nickname }
      );
      
      if (!playerProfile) {
        console.error('Failed to get player profile');
        return;
      }
      
      const playerId = playerProfile as number;

      const { error } = await supabase
        .from('online_room_players_cce0d86f_8f5')
        .update({ is_ready: !currentPlayer.is_ready })
        .eq('player_id', playerId);

      if (error) throw error;

      setPlayers(players.map(p => 
        p.player_nickname === currentPlayer.player_nickname
          ? { ...p, is_ready: !p.is_ready }
          : p
      ));
    } catch (error) {
      console.error('Failed to toggle ready:', error);
    }
  };

  const handleStartGame = async () => {
    try {
      await waitForSupabaseAuthReady();

      // Get current user's empId from localStorage (set by DingTalk platform)
      const empId = localStorage.getItem('dingtalk_emp_id');
      
      if (!empId) {
        alert('无法获取用户信息，请刷新页面重试');
        return;
      }

      // Call RPC to create game session and assign roles
      const { data, error } = await supabase.rpc('create_start_game_session', {
        p_room_id: roomInfo?.id,
        p_host_emp_id: empId
      });

      if (error) throw error;

      if (data && data.success) {
        // Navigate to game progress page
        const sessionId = data.session_id;
        window.location.href = `/online/game/${sessionId}?emp_id=${empId}`;
      } else {
        alert(data?.error || '开始游戏失败');
      }
      
    } catch (error) {
      console.error('Failed to start game:', error);
      alert('开始游戏失败，请重试');
    }
  };

  const canStartGame = () => {
    if (!roomInfo || !currentPlayer || !currentPlayer.is_host) return false;
    
    const seatedPlayers = players.filter(p => p.seat_number !== null && !p.is_host);
    if (seatedPlayers.length < 4) return false;
    
    return seatedPlayers.every(p => p.is_ready);
  };

  const isHost = currentPlayer?.is_host;
  const isPlayer = currentPlayer && !currentPlayer.is_host && !currentPlayer.is_spectator;
  const isSpectator = currentPlayer?.is_spectator || false;

  const renderSeats = () => {
    const maxSeats = playerCount;
    const seats = [];

    for (let i = 1; i <= maxSeats; i++) {
      const player = players.find(p => p.seat_number === i);
      const isCurrentPlayerSeat = player?.player_nickname === currentPlayer?.player_nickname;
      
      const avatarUrl = player && playerProfiles[player.player_id]?.avatar_url;

      seats.push(
        <div
          key={i}
          onClick={() => handleSeatClick(i)}
          className={`relative p-3 rounded-xl border-2 transition-all cursor-pointer ${
            isCurrentPlayerSeat
              ? 'bg-avalon-gold/20 border-avalon-gold'
              : player
                ? 'bg-avalon-card border-avalon-purple/50 hover:border-avalon-purple'
                : 'bg-avalon-card/50 border-dashed border-avalon-purple/30 hover:border-avalon-purple/60'
          }`}
        >
          <div className="flex flex-col items-center gap-2">
            {player ? (
              <>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-avalon-purple to-avalon-gold flex items-center justify-center overflow-hidden">
                  {avatarUrl && (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) ? (
                    <img src={avatarUrl} alt={player.player_nickname} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl">{player.player_nickname.charAt(0)}</span>
                  )}
                </div>
                <div className="text-center">
                  <div className="text-white text-xs font-medium truncate max-w-[80px]">
                    {player.player_nickname}
                  </div>
                  {player.is_host && (
                    <div className="flex items-center justify-center gap-0.5 mt-0.5">
                      <Crown className="w-3 h-3 text-avalon-gold" />
                      <span className="text-[10px] text-avalon-gold">房主</span>
                    </div>
                  )}
                  {player.is_ready && (
                    <div className="flex items-center justify-center gap-0.5 mt-0.5">
                      <Check className="w-3 h-3 text-green-400" />
                      <span className="text-[10px] text-green-400">已准备</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-avalon-card/50 flex items-center justify-center">
                  <UserPlus className="w-6 h-6 text-avalon-text" />
                </div>
                <div className="text-center">
                  <div className="text-avalon-text text-xs">空位</div>
                  <div className="text-avalon-text/50 text-[10px]">点击就位</div>
                </div>
              </>
            )}
            <div className="absolute top-1 left-2 text-[10px] text-avalon-text/50">
              {i}号
            </div>
          </div>

          {isHost && player && !player.is_host && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleKickPlayer(player.player_nickname);
              }}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center hover:bg-red-500/40 transition-colors"
              title="踢出玩家"
            >
              <X className="w-3 h-3 text-red-400" />
            </button>
          )}
        </div>
      );
    }

    return seats;
  };

  if (loading) {
    return (
      <ThemedBackground pageName="mission-vote">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-white text-xl">加载中...</div>
        </div>
      </ThemedBackground>
    );
  }

  return (
    <ThemedBackground pageName="mission-vote">
      <div className="min-h-screen p-5 pt-[72px] pb-8">
        {/* 顶部导航栏 */}
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-4 px-5 py-2" style={{ backgroundColor: '#1A0F2A', borderBottom: '1px solid #5A4BFF35' }}>
          <button
            onClick={() => {
              setShowLeaveConfirm(true);
            }}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-white hover:bg-avalon-purple transition-colors"
            title="返回大厅"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-white truncate">{roomInfo?.room_name || '游戏房间'}</h1>
            {roomInfo && (
              <p className="text-xs text-avalon-text truncate">
                {roomInfo.status === 'waiting' ? '等待中' : roomInfo.status === 'playing' ? '游戏中' : '已结束'}
              </p>
            )}
          </div>
          {currentPlayer && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-avalon-card">
              {currentPlayer.is_host && <Crown className="w-4 h-4 text-avalon-gold" />}
              <span className="text-white text-sm">{currentPlayer.player_nickname}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                currentPlayer.is_host ? 'bg-avalon-gold/20 text-avalon-gold' :
                currentPlayer.is_spectator ? 'bg-avalon-purple/20 text-avalon-purple' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {currentPlayer.is_host ? '房主' : 
                 currentPlayer.is_spectator ? '观战者' : '玩家'}
              </span>
              <button
                onClick={() => loadRoomData(true)}
                className="ml-1 p-1 rounded hover:bg-avalon-purple/50 transition-colors"
                title="刷新房间信息"
              >
                <RefreshCw className="w-3.5 h-3.5 text-avalon-text" />
              </button>
            </div>
          )}
        </div>

        {roomInfo && (
        <>
        {!configSectionCollapsed && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-avalon-text text-sm">游戏人数</div>
            {isGameLocked && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/30 text-green-400 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                已锁定
              </span>
            )}
          </div>
          <div className="grid grid-cols-5 gap-2 mb-2">
            {[5, 6, 7, 8, 9].map((count) => (
              <button
                key={count}
                onClick={() => updatePlayerCount(count)}
                disabled={isGameLocked || !currentPlayer?.is_host}
                className={`py-2 rounded-lg text-sm font-medium transition-all ${
                  playerCount === count
                    ? 'bg-avalon-gold text-avalon-bg-dark'
                    : 'bg-avalon-card text-white hover:bg-avalon-purple'
                } ${(isGameLocked || !currentPlayer?.is_host) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {count}人
              </button>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-2 mb-2">
            {[10, 11, 12, 13].map((count) => (
              <button
                key={count}
                onClick={() => updatePlayerCount(count)}
                disabled={isGameLocked || !currentPlayer?.is_host}
                className={`py-2 rounded-lg text-sm font-medium transition-all ${
                  playerCount === count
                    ? 'bg-avalon-gold text-avalon-bg-dark'
                    : 'bg-avalon-card text-white hover:bg-avalon-purple'
                } ${(isGameLocked || !currentPlayer?.is_host) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {count}人
              </button>
            ))}
            <div
              className={`py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                isGameLocked
                  ? 'bg-amber-500/30 text-amber-400'
                  : 'bg-green-500/30 text-green-400'
              }`}
            >
              {isGameLocked ? (
                <>
                  <Unlock className="w-4 h-4" />
                  <span>锁定</span>
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  <span>解锁</span>
                </>
              )}
            </div>
          </div>

        </div>
        )}

        <div className="avalon-card mb-4 py-2">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <User className="w-4 h-4 text-avalon-gold" />
              {configSectionCollapsed ? '游戏配置' : '角色配置'}
            </h3>
            {!configSectionCollapsed && (
              <button
                onClick={() => setShowRoleConfigModal(true)}
                disabled={isGameLocked || !currentPlayer?.is_host}
                className={`px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors ${
                  isGameLocked || !currentPlayer?.is_host
                    ? 'bg-avalon-purple text-white hover:bg-avalon-purple/70'
                    : 'bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <User className="w-3 h-3" />
                自定义配置
              </button>
            )}
            {configSectionCollapsed && (
              <button
                onClick={() => {
                  setConfigSectionCollapsed(false);
                  if (isGameLocked) {
                    setIsGameLocked(false);
                  }
                }}
                className="px-2 py-1 rounded-lg text-xs font-medium bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400 transition-colors"
              >
                展开配置
              </button>
            )}
          </div>

          {!configSectionCollapsed && (
          <div className="space-y-2">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-blue-400 text-sm font-medium">正义阵营</span>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {roleConfigs
                  .filter((c) => ROLES[c.roleId].faction === 'good' && c.count > 0)
                  .map((config) => {
                    const role = ROLES[config.roleId];
                    return (
                      <div
                        key={config.roleId}
                        className="p-1 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center gap-1"
                      >
                        <span className="text-sm">{role.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-[11px] font-medium truncate">
                            {role.name} <span className="text-avalon-gold">×{config.count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-red-400 text-sm font-medium">邪恶阵营</span>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {roleConfigs
                  .filter((c) => ROLES[c.roleId].faction === 'evil' && c.count > 0)
                  .map((config) => {
                    const role = ROLES[config.roleId];
                    return (
                      <div
                        key={config.roleId}
                        className="p-1 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center gap-1"
                      >
                        <span className="text-sm">{role.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-[11px] font-medium truncate">
                            {role.name} <span className="text-avalon-gold">×{config.count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="pt-1">
              <div className="flex items-center gap-2 mb-1">
                <Settings className="w-3.5 h-3.5 text-avalon-gold" />
                <span className="text-white font-medium text-xs">扩展规则</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30">
                    <span className="text-white text-xs">启用湖中仙女</span>
                    <input
                      type="checkbox"
                      checked={gameOptions.enableLadyOfLake}
                      onChange={(e) => updateGameOption('enableLadyOfLake', e.target.checked)}
                      className="w-3.5 h-3.5 rounded accent-avalon-gold"
                    />
                  </label>
                  <label className="flex-1 flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30">
                    <span className="text-white text-xs">启用王者之剑</span>
                    <input
                      type="checkbox"
                      checked={gameOptions.enableExcalibur}
                      onChange={(e) => updateGameOption('enableExcalibur', e.target.checked)}
                      className="w-3.5 h-3.5 rounded accent-avalon-gold"
                    />
                  </label>
                </div>
                <div className="flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30">
                  <span className="text-white text-xs">强制轮</span>
                  <div className="flex rounded-lg bg-avalon-card overflow-hidden border border-avalon-purple/30">
                    <button
                      onClick={() => updateGameOption('forcedRound', 'single3')}
                      className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        gameOptions.forcedRound === 'single3'
                          ? 'bg-avalon-gold text-avalon-bg-dark'
                          : 'text-white hover:bg-avalon-purple/50'
                      }`}
                    >
                      单轮 3 次
                    </button>
                    <button
                      onClick={() => updateGameOption('forcedRound', 'single5')}
                      className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        gameOptions.forcedRound === 'single5'
                          ? 'bg-avalon-gold text-avalon-bg-dark'
                          : 'text-white hover:bg-avalon-purple/50'
                      }`}
                    >
                      单轮 5 次
                    </button>
                    <button
                      onClick={() => updateGameOption('forcedRound', 'global3')}
                      className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        gameOptions.forcedRound === 'global3'
                          ? 'bg-avalon-gold text-avalon-bg-dark'
                          : 'text-white hover:bg-avalon-purple/50'
                      }`}
                    >
                      全局 3 次
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setConfigSectionCollapsed(true);
                if (!isGameLocked) {
                  setIsGameLocked(true);
                }
              }}
              className="w-full py-2 rounded-xl bg-avalon-gold text-avalon-bg-dark font-semibold text-sm hover:bg-yellow-400 transition-colors mt-1 shadow-lg"
            >
              确认配置并锁定
            </button>
          </div>
          )}
        </div>
        </>
        )}

        {/* 玩家座位区域 */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-semibold text-base flex items-center gap-2">
              <Shield className="w-5 h-5 text-avalon-gold" />
              玩家座位
            </h2>
            <div className="text-avalon-text text-sm">
              {players.filter(p => p.seat_number !== null).length} / {roomInfo?.max_players || 10} 已就位
            </div>
          </div>
          
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {renderSeats()}
          </div>
        </div>

        {/* 玩家列表 */}
        <div className="avalon-card mb-4 p-4">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <User className="w-4 h-4 text-avalon-gold" />
            游戏玩家（{players.filter(p => !p.is_spectator).length}人）
          </h3>
          <div className="space-y-2">
            {players.filter(p => !p.is_spectator).map((player) => {
              const avatarUrl = playerProfiles[player.player_id]?.avatar_url;
              return (
              <div
                key={player.player_nickname}
                className="flex items-center justify-between p-2 rounded-lg bg-avalon-bg-dark/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-avalon-purple to-avalon-gold flex items-center justify-center overflow-hidden">
                    {avatarUrl && (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) ? (
                      <img src={avatarUrl} alt={player.player_nickname} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm text-white">{player.player_nickname.charAt(0)}</span>
                    )}
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">{player.player_nickname}</div>
                    <div className="text-avalon-text text-xs">
                      {player.seat_number ? `${player.seat_number}号座位` : '未就位'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {player.is_host && (
                    <div className="flex items-center gap-1 text-avalon-gold">
                      <Crown className="w-3 h-3" />
                      <span className="text-xs">房主</span>
                    </div>
                  )}
                  {player.is_ready && (
                    <div className="flex items-center gap-1 text-green-400">
                      <Check className="w-3 h-3" />
                      <span className="text-xs">已准备</span>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* 观战者列表 */}
        {players.filter(p => p.is_spectator).length > 0 && (
          <div className="avalon-card mb-4 p-4">
            <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-avalon-purple" />
              观战者（{players.filter(p => p.is_spectator).length}人）
            </h3>
            <div className="space-y-2">
              {players.filter(p => p.is_spectator).map((player) => {
                const avatarUrl = playerProfiles[player.player_id]?.avatar_url;
                return (
                <div
                  key={player.player_nickname}
                  className="flex items-center justify-between p-2 rounded-lg bg-avalon-bg-dark/30"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-avalon-purple to-pink-500 flex items-center justify-center overflow-hidden">
                      {avatarUrl && (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) ? (
                        <img src={avatarUrl} alt={player.player_nickname} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm text-white">{player.player_nickname.charAt(0)}</span>
                      )}
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">{player.player_nickname}</div>
                      <div className="text-avalon-text text-xs">正在观看比赛</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-avalon-purple">
                    <Eye className="w-3 h-3" />
                    <span className="text-xs">观战中</span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 底部操作按钮 */}
        <div className="fixed bottom-0 left-0 right-0 p-4 z-40" style={{ backgroundColor: '#1A0F2A', borderTop: '1px solid #5A4BFF35' }}>
          {/* 房主操作按钮 */}
          {isHost ? (
            <div className="flex gap-3 max-w-md mx-auto mb-4">
              <button
                onClick={handleStartGame}
                disabled={!canStartGame()}
                className={`flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  canStartGame()
                    ? 'bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400'
                    : 'bg-avalon-card text-avalon-text cursor-not-allowed'
                }`}
              >
                <Play className="w-5 h-5" />
                开始游戏
              </button>
              <button
                onClick={() => setShowCloseRoomConfirm(true)}
                className="px-6 py-3 rounded-xl font-semibold bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                关闭房间
              </button>
            </div>
          ) : null}

          {/* 玩家操作按钮 */}
          {isPlayer ? (
            <div className="flex gap-3 max-w-md mx-auto">
              <button
                onClick={handleToggleReady}
                className={`flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  currentPlayer?.is_ready
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    : 'bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400'
                }`}
              >
                {currentPlayer?.is_ready ? (
                  <>
                    <Check className="w-5 h-5" />
                    取消准备
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    准备就绪
                  </>
                )}
              </button>
              <button
                onClick={handleExitRoom}
                className="px-6 py-3 rounded-xl font-semibold bg-avalon-card text-white hover:bg-avalon-purple transition-colors flex items-center gap-2"
              >
                <LogOut className="w-5 h-5" />
                退出房间
              </button>
            </div>
          ) : null}

          {/* 观战者操作按钮 */}
          {isSpectator ? (
            <div className="flex gap-3 max-w-md mx-auto">
              <div className="flex-1 py-3 rounded-xl font-semibold text-center bg-avalon-purple/20 text-avalon-purple border border-avalon-purple/50">
                👁️ 观战模式中
              </div>
              <button
                onClick={handleExitRoom}
                className="px-6 py-3 rounded-xl font-semibold bg-avalon-card text-white hover:bg-avalon-purple transition-colors flex items-center gap-2"
              >
                <LogOut className="w-5 h-5" />
                离开观战
              </button>
            </div>
          ) : null}

          {/* 未加入提示 */}
          {!currentPlayer && (
            <div className="text-center text-avalon-text text-sm">
              您尚未加入此房间
            </div>
          )}
        </div>

        {/* 角色配置弹窗 */}
        <RoleConfigModal
          isOpen={showRoleConfigModal}
          onClose={() => setShowRoleConfigModal(false)}
          playerCount={playerCount}
          roleConfigs={roleConfigs}
          onSave={saveRoleConfigs}
        />
        
        {/* 离开房间确认对话框 */}
        {showLeaveConfirm && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowLeaveConfirm(false)}
          >
            <div
              className="rounded-2xl p-6 max-w-md w-full avalon-card bg-avalon-card border border-avalon-purple/30"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">确认离开房间</h3>
                <button
                  onClick={() => setShowLeaveConfirm(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-avalon-purple/20 transition-colors"
                >
                  <svg className="w-5 h-5 text-avalon-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                {currentPlayer?.is_host && players.filter(p => !p.is_spectator && p.player_nickname !== currentPlayer.player_nickname).length > 0 ? (
                  <>
                    <p className="text-amber-400 mb-2">⚠️ 您是房主且房间内已有其他玩家</p>
                    <p className="text-avalon-text text-sm">您确定要离开吗？房主离开后房间将自动解散，其他玩家将被请出房间。</p>
                  </>
                ) : (
                  <p className="text-avalon-text text-sm">您确定要离开这个房间吗？</p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowLeaveConfirm(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-avalon-purple/30 text-avalon-text hover:bg-avalon-purple/20 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    setShowLeaveConfirm(false);
                    handleExitRoom();
                  }}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/30 transition-colors"
                >
                  确认离开
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 关闭房间确认对话框 */}
        {showCloseRoomConfirm && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCloseRoomConfirm(false)}
          >
            <div
              className="rounded-2xl p-6 max-w-md w-full avalon-card bg-avalon-card border border-avalon-purple/30"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">确认关闭房间</h3>
                <button
                  onClick={() => setShowCloseRoomConfirm(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-avalon-purple/20 transition-colors"
                >
                  <svg className="w-5 h-5 text-avalon-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <p className="text-amber-400 mb-2">⚠️ 关闭房间后所有玩家将被请出</p>
                <p className="text-avalon-text text-sm">您确定要关闭并解散这个房间吗？此操作不可恢复。</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCloseRoomConfirm(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-avalon-purple/30 text-avalon-text hover:bg-avalon-purple/20 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    setShowCloseRoomConfirm(false);
                    handleCloseRoom();
                  }}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/30 transition-colors"
                >
                  确认关闭
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ThemedBackground>
  );
}
